import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardChat from "../components/Dashboard/DashboardChat";
import DashboardTileGrid from "../components/Dashboard/DashboardTileGrid";
import NewDashboardModal from "../components/Dashboard/NewDashboardModal";
import DashboardChangelogModal from "../components/Dashboard/DashboardChangelogModal";
import DashboardTabs from "../components/Dashboard/DashboardTabs";
import DashboardToolbar from "../components/Dashboard/DashboardToolbar";
import NoDashboardsState from "../components/Dashboard/NoDashboardsState";
import EmptyDashboardState from "../components/Dashboard/EmptyDashboardState";
import ScheduledDashboardTab from "../components/Dashboard/ScheduledDashboardTab";
import DashboardLibraryModal from "../components/Dashboard/DashboardLibraryModal";
import { LoadingText } from "../components/ui";
import { useClock, useDashboards } from "../hooks";
import { useAuth } from "../hooks/useAuth";
import { hasRole, ANALYST_ROLES } from "../constants/roles";
import { userReportsApi } from "../api/userReports";
import type { UserDashboard, UserWorkspace } from "../types/reports";

export default function DashboardPage() {
  const navigate = useNavigate();
  const time = useClock();
  const { user } = useAuth();
  const canSchedule = hasRole(user, ANALYST_ROLES);

  const {
    dashboards,
    activeTab,
    tiles,
    tilesLoading,
    dashboardsLoading,
    refreshKey,
    renamingId,
    renameValue,
    activeLabel,
    setActiveTab,
    setRenameValue,
    refreshAll,
    reorder,
    saveTileLayouts,
    handleDashboardCreated,
    startRename,
    cancelRename,
    commitRename,
    handleDeleteDashboard,
    workspaces,
    workspacesLoading,
    activeWorkspace,
    setActiveWorkspace,
    scheduledDashboards,
    activeTabIsScheduled,
    dashboardsForWorkspace,
    createWorkspace,
    renameWorkspace,
    deleteWorkspace,
    moveToWorkspace,
    promoteUnassigned,
  } = useDashboards();

  const [showNewDashboard, setShowNewDashboard] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showChat, setShowChat] = useState(false);

  // Close Brian chat when switching tabs
  useEffect(() => { setShowChat(false); }, [activeTab]);
  const [movePopover, setMovePopover] = useState<{ tabId: string; isScheduled: boolean } | null>(null);
  const [wsDeleteModal, setWsDeleteModal] = useState<{ id: string; name: string } | null>(null);
  const [wsDeleteTarget, setWsDeleteTarget] = useState<string>("__unassign__");
  const [wsDeleting, setWsDeleting] = useState(false);

  const goToReports = () => navigate("/reports");

  const currentDashboard = activeTab && !activeTabIsScheduled
    ? dashboards.find((d) => d.id === activeTab)
    : null;

  const handleShareToggle = async () => {
    if (!currentDashboard) return;
    await userReportsApi.updateDashboard(currentDashboard.id, {
      is_shared: !currentDashboard.is_shared,
    });
    refreshAll();
  };

  const handleWorkspaceDelete = (id: string) => {
    const ws = workspaces.find((w) => w.id === id);
    if (!ws) return;
    setWsDeleteTarget("__unassign__");
    setWsDeleteModal({ id, name: ws.name });
  };

  const handleWorkspaceDeleteConfirm = async () => {
    if (!wsDeleteModal) return;
    setWsDeleting(true);
    try {
      const toMove = dashboards.filter((d) => d.workspace_id === wsDeleteModal.id);
      if (wsDeleteTarget === "__delete__") {
        // Delete all dashboards in the workspace first
        await Promise.all(toMove.map((d) => userReportsApi.deleteDashboard(d.id)));
      } else if (wsDeleteTarget !== "__unassign__") {
        // Move dashboards to another workspace
        await Promise.all(
          toMove.map((d) => userReportsApi.updateDashboard(d.id, { workspace_id: wsDeleteTarget }))
        );
      }
      // __unassign__: dashboards keep workspace_id=null after workspace delete (cascade SET NULL)
      await deleteWorkspace(wsDeleteModal.id);
      setWsDeleteModal(null);
    } finally {
      setWsDeleting(false);
    }
  };

  const [newWsModalOpen, setNewWsModalOpen] = useState(false);
  const [newWsName, setNewWsName] = useState("");
  const [newWsCreating, setNewWsCreating] = useState(false);
  const newWsInputRef = useRef<HTMLInputElement>(null);

  const handleWorkspaceCreate = () => {
    setNewWsName("");
    setNewWsModalOpen(true);
    setTimeout(() => newWsInputRef.current?.focus(), 50);
  };

  const handleWorkspaceCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newWsName.trim();
    if (!name) return;
    setNewWsCreating(true);
    try {
      const ws = await createWorkspace(name);
      setActiveWorkspace(ws.id);
      setNewWsModalOpen(false);
    } finally {
      setNewWsCreating(false);
    }
  };

  const handleMoveTab = (tabId: string, isScheduled: boolean) => {
    setMovePopover({ tabId, isScheduled });
  };

  const handleMoveTo = async (targetWorkspaceId: string | null) => {
    if (!movePopover) return;
    await moveToWorkspace(movePopover.tabId, movePopover.isScheduled, targetWorkspaceId);
    setMovePopover(null);
  };

  const tabsForActiveWorkspace = dashboardsForWorkspace(activeWorkspace);

  const activeSchedId = activeTabIsScheduled && activeTab
    ? parseInt(activeTab.replace("sched-", ""), 10)
    : null;

  if (dashboardsLoading || workspacesLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingText />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <DashboardTabs
        workspaces={workspaces}
        activeWorkspace={activeWorkspace}
        hasScheduledDashboards={scheduledDashboards.length > 0}
        onWorkspaceSelect={setActiveWorkspace}
        onWorkspaceCreate={handleWorkspaceCreate}
        onWorkspaceRename={renameWorkspace}
        onWorkspaceDelete={handleWorkspaceDelete}
        onUnassignedRename={promoteUnassigned}
        onLibraryOpen={() => setShowLibrary(true)}
        canSchedule={canSchedule}
        onScheduleOpen={() => navigate("/scheduled-dashboards")}
        tabs={tabsForActiveWorkspace}
        activeTab={activeTab}
        onTabSelect={setActiveTab}
        onTabCreate={() => setShowNewDashboard(true)}
        onTabRename={(id, name) => {
          setRenameValue(name);
          commitRename(id);
        }}
        onTabDelete={handleDeleteDashboard}
        onTabReorder={reorder}
        onTabMove={handleMoveTab}
        renamingId={renamingId}
        renameValue={renameValue}
        onRenameValueChange={setRenameValue}
        onRenameStart={(id) => {
          const dash = dashboards.find((d) => d.id === id);
          if (dash) startRename(dash);
          else { /* fallback */ }
        }}
        onRenameCancel={cancelRename}
      />

      {/* Move-to-workspace popover */}
      {movePopover && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20 p-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-xl min-w-[200px]">
            <p className="mb-2 text-sm font-semibold text-gray-800">Move to workspace</p>
            <button
              onClick={() => handleMoveTo(null)}
              className="flex w-full rounded-lg px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              No workspace (unassigned)
            </button>
            {workspaces.map((ws: UserWorkspace) => (
              <button
                key={ws.id}
                onClick={() => handleMoveTo(ws.id)}
                className="flex w-full rounded-lg px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                {ws.name}
              </button>
            ))}
            <button
              onClick={() => setMovePopover(null)}
              className="mt-2 flex w-full rounded-lg px-3 py-2 text-left text-xs text-gray-400 hover:text-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {activeTab !== null && !activeTabIsScheduled && (
        <DashboardChat
          dashboardId={activeTab}
          onWidgetAdded={() => { refreshAll(); setShowChat(false); }}
          open={showChat}
          onClose={() => setShowChat(false)}
        />
      )}

      <div className="flex-1 overflow-y-auto">
        {activeTabIsScheduled && activeSchedId !== null ? (
          <ScheduledDashboardTab
            dashboardId={activeSchedId}
            dashboardName={activeLabel}
          />
        ) : dashboards.length === 0 ? (
          <NoDashboardsState onNew={() => setShowNewDashboard(true)} />
        ) : tilesLoading ? (
          <div className="flex h-full items-center justify-center">
            <LoadingText />
          </div>
        ) : tiles.length === 0 ? (
          <EmptyDashboardState
            tabName={activeLabel}
            onNew={() => setShowNewDashboard(true)}
            onManageReports={goToReports}
            onOpenLibrary={() => setShowLibrary(true)}
          />
        ) : (
          <div className="p-4 md:p-6">
            <DashboardToolbar
              onRefresh={refreshAll}
              onOpenChangelog={() => setChangelogOpen(true)}
              onManageReports={goToReports}
              isShared={currentDashboard?.is_shared ?? false}
              onShareToggle={handleShareToggle}
              onBrianOpen={() => setShowChat((o) => !o)}
              brianOpen={showChat}
            />

            <DashboardTileGrid
              tiles={tiles}
              refreshKey={refreshKey}
              onLayoutStop={saveTileLayouts}
              onUnpinned={refreshAll}
            />

            {currentDashboard && (
              <DashboardFooter
                tileCount={tiles.length}
                createdAt={currentDashboard.created_at}
                updatedAt={currentDashboard.updated_at ?? null}
                ownerName={currentDashboard.owner_name ?? ""}
                dataAsOf={time}
              />
            )}
          </div>
        )}
      </div>

      {showNewDashboard && (
        <NewDashboardModal
          onClose={() => setShowNewDashboard(false)}
          onCreated={(dashboard: UserDashboard) => {
            handleDashboardCreated(dashboard);
            setShowNewDashboard(false);
          }}
          workspaceId={
            activeWorkspace !== "__unassigned__" && activeWorkspace !== "__scheduled__"
              ? activeWorkspace
              : null
          }
        />
      )}

      {changelogOpen && activeTab && !activeTabIsScheduled && (
        <DashboardChangelogModal
          dashboardId={activeTab}
          dashboardName={activeLabel}
          onClose={() => setChangelogOpen(false)}
        />
      )}

      {showLibrary && (
        <DashboardLibraryModal
          onClose={() => setShowLibrary(false)}
          workspaces={workspaces}
          onDashboardAdded={(dashboard) => {
            handleDashboardCreated(dashboard);
          }}
        />
      )}

      {wsDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="mb-1 text-base font-semibold text-gray-900">Delete workspace</h2>
            <p className="mb-4 text-sm text-gray-500">
              Delete <span className="font-medium text-gray-800">"{wsDeleteModal.name}"</span>? Choose what to do with its dashboards:
            </p>
            <div className="mb-4 space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="wsDeleteTarget"
                  value="__unassign__"
                  checked={wsDeleteTarget === "__unassign__"}
                  onChange={() => setWsDeleteTarget("__unassign__")}
                  className="mt-0.5"
                />
                <span className="text-sm text-gray-700">Keep dashboards (unassigned)</span>
              </label>
              {workspaces.filter((w) => w.id !== wsDeleteModal.id).map((ws) => (
                <label key={ws.id} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="wsDeleteTarget"
                    value={ws.id}
                    checked={wsDeleteTarget === ws.id}
                    onChange={() => setWsDeleteTarget(ws.id)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-gray-700">Move to <span className="font-medium">{ws.name}</span></span>
                </label>
              ))}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="wsDeleteTarget"
                  value="__delete__"
                  checked={wsDeleteTarget === "__delete__"}
                  onChange={() => setWsDeleteTarget("__delete__")}
                  className="mt-0.5"
                />
                <span className="text-sm text-red-600">Delete all dashboards in this workspace</span>
              </label>
            </div>
            {wsDeleteTarget === "__delete__" && (
              <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
                This will permanently delete all dashboards and their tiles. This cannot be undone.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setWsDeleteModal(null)}
                disabled={wsDeleting}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleWorkspaceDeleteConfirm}
                disabled={wsDeleting}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {wsDeleting ? "Deleting…" : "Delete workspace"}
              </button>
            </div>
          </div>
        </div>
      )}

      {newWsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-base font-semibold text-gray-900">New workspace</h2>
            <form onSubmit={handleWorkspaceCreateSubmit}>
              <input
                ref={newWsInputRef}
                type="text"
                value={newWsName}
                onChange={(e) => setNewWsName(e.target.value)}
                placeholder="Workspace name"
                maxLength={80}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#1D345F] focus:outline-none"
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setNewWsModalOpen(false)}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newWsName.trim() || newWsCreating}
                  className="rounded-lg bg-[#1D345F] px-4 py-2 text-sm font-medium text-white hover:bg-[#162847] disabled:opacity-50"
                >
                  {newWsCreating ? "Creating…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Dashboard footer ───────────────────────────────────────────────────────────

/// <reference types="vite/client" />
const ENV_LABEL = import.meta.env.VITE_APP_ENV === "production" ? "live" : "Staging";

function DashboardFooter({
  tileCount,
  createdAt,
  updatedAt,
  ownerName,
  dataAsOf,
}: {
  tileCount: number;
  createdAt: string;
  updatedAt: string | null;
  ownerName: string;
  dataAsOf: string;
}) {
  function fmt(iso: string) {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  }

  return (
    <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-gray-100 pt-3 text-xs text-gray-400">
      <span>Data as of {dataAsOf}</span>
      <span>·</span>
      <span className={ENV_LABEL === "live" ? "text-green-500 font-medium" : "text-amber-500 font-medium"}>
        {ENV_LABEL}
      </span>
      <span>·</span>
      <span>{tileCount} tile{tileCount !== 1 ? "s" : ""}</span>
      <span>·</span>
      <span>Created {fmt(createdAt)}</span>
      {updatedAt && (
        <>
          <span>·</span>
          <span>Last updated {fmt(updatedAt)}</span>
        </>
      )}
      {ownerName && (
        <>
          <span>·</span>
          <span>By {ownerName}</span>
        </>
      )}
    </div>
  );
}
